import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { getMafiaEmpId, getMafiaEmployeeName } from "./mafiaIdentity";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";
import MafiaStatusTimeline from "./MafiaStatusTimeline";

export default function ConfirmPart({
  partNum = "",
  rev = 0,
  revMinor = 0,
  onBack,
  onConfirmed,
}) {
  const [username, setUsername] = useState("");
  const [initials, setInitials] = useState("");
  const [history, setHistory] = useState("");
  const [rows, setRows] = useState([]);
  const [txtInitials, setTxtInitials] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDisabled, setConfirmDisabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");

  const usernameRef = useRef("");
  const lockAcquiredRef = useRef(false);
  const mountedRef = useRef(true);
  const dialog = useAppDialog();

  const pageTitle = useMemo(() => {
    return `${partNum || "—"} ${rev}.${revMinor}`;
  }, [partNum, rev, revMinor]);

  const styledRows = useMemo(() => {
    return (rows || []).map((row) => ({
      ...row,
      __additionalInfoClass: getAdditionalInfoClass(row),
    }));
  }, [rows]);

  const releaseLock = useCallback(async () => {
    try {
      const currentUsername = usernameRef.current;
      if (!currentUsername || !lockAcquiredRef.current || !partNum) return;

      const currentLockStatus = await getLockStatus({
        partNumber: partNum,
        rev,
        revMinor,
      });

      if (
        String(currentLockStatus || "").trim().toLowerCase() ===
        String(currentUsername || "").trim().toLowerCase()
      ) {
        await setLockStatus({
          partNumber: partNum,
          rev,
          revMinor,
          lockStatus: "UNLOCK",
        });
      }
    } catch (error) {
      console.error("Failed to release lock:", error);
    } finally {
      lockAcquiredRef.current = false;
    }
  }, [partNum, rev, revMinor]);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");
      setConfirmDisabled(false);

      if (!partNum) {
        setRows([]);
        setHistory("");
        setInitials("");
        return;
      }

      const currentUser = await getCurrentUsername();
      if (!mountedRef.current) return;

      setUsername(currentUser);
      usernameRef.current = currentUser;

      const lockStatus = await getLockStatus({
        partNumber: partNum,
        rev,
        revMinor,
      });
      if (!mountedRef.current) return;

      const normalizedLock = String(lockStatus || "").trim().toLowerCase();
      const normalizedUser = String(currentUser || "").trim().toLowerCase();

      if (normalizedLock && normalizedLock !== normalizedUser) {
        setPageError(`This part is already being checked by ${lockStatus}.`);
        setRows([]);
        return;
      }

      if (!normalizedLock || normalizedLock === normalizedUser) {
        await setLockStatus({
          partNumber: partNum,
          rev,
          revMinor,
          lockStatus: normalizedUser,
        });

        lockAcquiredRef.current = true;
      }

      const [oldHistory, partRows] = await Promise.all([
        getOldHistory({
          partNumber: partNum,
          rev,
          revMinor,
        }),
        getPart({
          partNumber: partNum,
          rev,
          revMinor,
        }),
      ]);

      if (!mountedRef.current) return;

      const safeHistory = oldHistory || "";
      const safeRows = partRows || [];
      // Round 16/17: initials are computed from the human display name
      // (e.g. "Akash Sharma" -> "AS"), not from the EmpId. Feeding the EmpId
      // (e.g. "3325") produced garbage initials. The legacy `konkle -> "CMK"`
      // hardcode is removed; initials-from-name handles every user uniformly.
      const displayName = await getCurrentDisplayName();
      const computedInitials = computeInitials(displayName);
      const lastHistoryUser = getLastHistoryUser(safeHistory);

      setHistory(safeHistory);
      setRows(safeRows);
      setInitials(computedInitials);

      if (
        lastHistoryUser &&
        normalizedUser === String(lastHistoryUser || "").trim().toLowerCase()
      ) {
        setConfirmDisabled(true);
        setTxtInitials("");
        setPageError("You may not confirm this part because you were the last one to modify it.");
      }
    } catch (error) {
      console.error("Failed to load ConfirmPart:", error);
      if (mountedRef.current) {
        setPageError("Failed to load part confirmation page.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [partNum, rev, revMinor]);

  useEffect(() => {
    mountedRef.current = true;
    loadPage();

    return () => {
      mountedRef.current = false;
      releaseLock();
    };
  }, [loadPage, releaseLock]);

  async function handleConfirm() {
    if (confirmDisabled || submitting) return;

    if (txtInitials.trim().toLowerCase() !== initials.trim().toLowerCase()) {
      await dialog.warning(
        `Please enter your initials of ${initials.toUpperCase()} in the box provided to continue.`
      );
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const updatedHistory = username.toLowerCase();

      const confirmed = await confirmPart({
        partNum,
        revMajor: rev,
        revMinor,
        history: updatedHistory,
        oldHistory: history,
        confirmedBy: username.toLowerCase(),
      });

      if (!confirmed) {
        setPageError("Someone else has already confirmed this part.");
        return;
      }

      await Promise.all([
        sendEmailPartConfirmed({
          fromName: username,
          partNum,
          revMajor: rev,
          revMinor,
        }),
        sendEmailNeedsRelease({
          fromName: username,
          partNum,
          revMajor: rev,
          revMinor,
        }),
      ]);

      await releaseLock();

      await dialog.success("Part Confirmed.");

      if (onConfirmed) {
        onConfirmed({
          partNum,
          rev,
          revMinor,
        });
        return;
      }

      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Confirm part failed:", error);
      setPageError("Failed to confirm part.");
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4 text-muted">Loading confirmation workspace...</CardBody>
      </Card>
    );
  }

  return (
    <div className="confirm-part-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        compact
        icon="ri-check-double-line"
        eyebrow="MAFIA Aux"
        title="Confirm Part"
        subtitle={
          <>
            <strong style={{ color: "rgba(255,255,255,0.95)" }}>{partNum || "—"}</strong>{" "}
            · Rev {rev}.{revMinor}
            {rows.length ? ` · ${rows.length} row${rows.length === 1 ? "" : "s"}` : ""}
          </>
        }
        tone="amber"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Confirm Part" },
        ]}
      />

      <MafiaStatusTimeline history={history} current="confirming" />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="confirm-part-toolbar-main">
                <div className="confirm-part-toolbar-main-left">
                  <Button
                    color="success"
                    className="confirm-part-action-btn"
                    onClick={handleConfirm}
                    disabled={confirmDisabled || submitting || !rows.length}
                  >
                    <i className="ri-check-double-line confirm-part-btn-icon me-1" />
                    {submitting ? "Confirming..." : "Confirm Part"}
                  </Button>
                </div>

                <div className="confirm-part-toolbar-main-right">
                  <InputGroup className="confirm-part-initials-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-user-line" />
                    </InputGroupText>
                    <Input
                      id="txtInitials"
                      type="text"
                      value={txtInitials}
                      onChange={(e) => setTxtInitials(e.target.value)}
                      className="border-start-0"
                      placeholder={initials ? `Enter ${initials.toUpperCase()} to confirm` : "Enter initials"}
                      disabled={confirmDisabled || submitting}
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="confirm-part-toolbar-utility">
                <Badge color="light" className="confirm-part-info-badge">
                  User: {username || "—"}
                </Badge>

                <div className="confirm-part-info-pill">
                  <span className="confirm-part-info-label">Part</span>
                  <span className="confirm-part-info-value" title={partNum || "—"}>
                    {partNum || "—"}
                  </span>
                </div>

                <div className="confirm-part-info-pill">
                  <span className="confirm-part-info-label">Revision</span>
                  <span className="confirm-part-info-value">
                    {rev}.{revMinor}
                  </span>
                </div>

                <div className="confirm-part-info-pill">
                  <span className="confirm-part-info-label">Rows Loaded</span>
                  <span className="confirm-part-info-value">{styledRows.length}</span>
                </div>

                <div className="confirm-part-refresh-note">
                  Confirm with initials <strong>{initials || "—"}</strong>
                </div>
              </div>

              {pageError ? (
                <div className="confirm-part-error-banner mt-3">
                  <i className="ri-error-warning-line me-2" />
                  {pageError}
                </div>
              ) : null}
            </CardHeader>

            <CardBody className="p-4">
              <div className="confirm-part-table-shell">
                <div className="confirm-part-table-scroll">
                  <Table responsive={false} className="align-middle mb-0 confirm-part-table">
                    <thead className="table-light">
                      <tr>
                        <HeaderCell label="DNA" />
                        <HeaderCell label="Dimension Type" />
                        <HeaderCell label="Unit" />
                        <HeaderCell label="Nominal" />
                        <HeaderCell label="Min" />
                        <HeaderCell label="Max" />
                        <HeaderCell label="Tolerance Class" />
                        <HeaderCell label="In Process" />
                        <HeaderCell label="Method Of Recording" />
                        <HeaderCell label="Special Instructions" />
                        <HeaderCell label="WI or ATP Number" />
                        <HeaderCell label="Gages" />
                        <HeaderCell label="Additional Info" />
                        <HeaderCell label="Mom Op #" />
                      </tr>
                    </thead>
                    <tbody>
                      {styledRows.length === 0 ? (
                        <tr>
                          <td colSpan={14} className="text-center py-5 text-muted">
                            {pageError ? "Unable to load part data." : "No data found."}
                          </td>
                        </tr>
                      ) : (
                        styledRows.map((row, index) => (
                          <tr key={`${row.DNA}-${index}`} className="confirm-part-data-row">
                            <Cell value={row.DNA} />
                            <Cell value={row.DimensionType} />
                            <Cell value={row.Unit} />
                            <Cell value={row.Nominal} />
                            <Cell value={row.Min} />
                            <Cell value={row.Max} />
                            <Cell value={row.ToleranceClass} />
                            <Cell value={row.InProcess} />
                            <Cell value={row.MethodOfRecording} />
                            <Cell value={row.SpecialInstructions} />
                            <Cell value={row.WIATPNumber} />
                            <Cell value={row.Gages} />
                            <Cell value={row.AdditionalInfo} className={row.__additionalInfoClass} />
                            <Cell value={row.MOMOpNumber} />
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>
        {`
          .confirm-part-toolbar-main {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            flex-wrap: wrap;
            margin-bottom: 0.9rem;
          }

          .confirm-part-toolbar-main-left {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
          }

          .confirm-part-toolbar-main-right {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex: 0 0 360px;
            min-width: 280px;
          }

          .confirm-part-toolbar-utility {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
            padding-top: 0.25rem;
          }

          .confirm-part-initials-group {
            width: 100%;
          }

          .confirm-part-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .confirm-part-action-btn:disabled,
          .confirm-part-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


          .confirm-part-btn-icon {
            font-size: 0.95rem;
          }

          .confirm-part-info-badge {
            display: inline-flex;
            align-items: center;
            min-height: 38px;
            border: 1px solid #e9ebec;
            padding: 0.58rem 0.8rem;
            border-radius: 0.65rem;
            font-size: 0.85rem;
            color: #212529;
            background: #f8fafc;
          }

          .confirm-part-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .confirm-part-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

          .confirm-part-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .confirm-part-refresh-note {
            min-height: 38px;
            display: inline-flex;
            align-items: center;
            padding: 0.45rem 0.2rem;
            font-size: 0.84rem;
            color: #878a99;
            font-weight: 500;
          }

          .confirm-part-error-banner {
            display: flex;
            align-items: center;
            padding: 0.8rem 0.95rem;
            border-radius: 0.8rem;
            border: 1px solid rgba(220, 53, 69, 0.18);
            background: rgba(220, 53, 69, 0.06);
            color: #842029;
            font-weight: 500;
          }

          .btn-soft-dark {
            background: #eff2f7;
            color: #212529;
            border: 1px solid #e9ebec;
          }

          .btn-soft-dark:hover,
          .btn-soft-dark:focus {
            background: #e9ebec;
            color: #212529;
            border-color: #dee2e6;
          }

          .confirm-part-table-shell {
            border: 1px solid #e9ebec;
            border-radius: 1rem;
            background: #fff;
          }

          .confirm-part-table-scroll {
          max-height: calc(100vh - 260px);
          min-height: 320px;
          overflow-x: auto;
          overflow-y: auto;
        }

          .confirm-part-table {
            min-width: 1700px;
          }

          .confirm-part-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #f3f6f9;
            white-space: nowrap;
          }

          .confirm-part-table tbody td {
            padding: 0.9rem 0.9rem;
            border-top: 1px solid #eef2f7;
            color: #495057;
            vertical-align: top;
          }

          .confirm-part-data-row:nth-of-type(even) {
            background: #fbfcfe;
          }

          .confirm-part-data-row:hover {
            background: #f5f9ff;
          }

          .confirm-part-additional-reference {
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            font-size: 13px;
          }

          .confirm-part-additional-note {
            font-family: inherit;
          }

          .confirm-part-additional-gdt {
            font-weight: 700;
            letter-spacing: 0.04em;
          }

          .confirm-part-additional-default {
            font-family: Georgia, serif;
          }

          @media (max-width: 1199.98px) {
            .confirm-part-toolbar-main {
              flex-direction: column;
              align-items: stretch;
            }

            .confirm-part-toolbar-main-right {
              flex: 1 1 auto;
              min-width: 100%;
            }
          }

          @media (max-width: 991.98px) {
            .confirm-part-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

            .confirm-part-toolbar-utility {
              align-items: stretch;
            }
          }
        `}
      </style>
    </div>
  );
}

function HeaderCell({ label }) {
  return <th>{label}</th>;
}

function Cell({ value, className = "" }) {
  return <td className={className}>{value ?? ""}</td>;
}

function computeInitials(displayName) {
  // Round 16/17: derive initials from the human display name. Supports
  // "First Last" (-> "FL") and the legacy "first.last" form (-> "FL").
  // The previous `konkle -> "CMK"` hardcode is gone; this handles all users.
  const name = (displayName || "").trim();
  if (!name) return "";

  // Split on whitespace, dots, hyphens, commas, underscores.
  const parts = name.split(/[\s._\-,]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  // Single token: first two letters as a reasonable fallback.
  return parts.length === 1
    ? parts[0].substring(0, 2).toUpperCase()
    : "";
}

function getLastHistoryUser(history) {
  let last = "";

  if (!history || !history.trim()) {
    return last;
  }

  if (history.includes("(") && history.includes(")")) {
    const rep = history.substring(0, history.lastIndexOf(")"));
    last = history.replace(rep, "");
    last = last.replace(";", "");
    last = last.replace(")", "");
    last = last.trim();
    return last;
  }

  if (history.includes(";")) {
    const rep = history.substring(0, history.lastIndexOf(";"));
    last = history.replace(rep, "");
    last = last.replace(";", "");
    last = last.trim();
    return last;
  }

  return "";
}

function getAdditionalInfoClass(row) {
  const dimensionType = String(row.DimensionType || "").toLowerCase().trim();
  const toleranceClass = String(row.ToleranceClass || "").toLowerCase().trim();

  if (toleranceClass.includes("reference")) {
    return "confirm-part-additional-reference";
  }

  if (
    dimensionType.includes("note") ||
    dimensionType.includes("*") ||
    dimensionType.includes("finish")
  ) {
    return "confirm-part-additional-note";
  }

  if (dimensionType.includes("gd&t")) {
    return "confirm-part-additional-gdt";
  }

  return "confirm-part-additional-default";
}

async function getCurrentUsername() {
  return getMafiaEmpId();
}

async function getCurrentDisplayName() {
  // Round 17: human name for initials/label, from empbasic.Name via whoami.
  const name = await getMafiaEmployeeName();
  return name || "";
}

async function getLockStatus(request) {
  const response = await api.post("mafia/lockstatus", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });

  return response.data;
}

async function setLockStatus(request) {
  const response = await api.post("mafia/set-lock-status", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
    newLock: request.lockStatus,
  });

  return response.data;
}

async function getOldHistory(request) {
  const response = await api.post("mafia/old-history", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });
  console.log(response.data, 'getOldHistory');
  return response.data;
}

async function getPart(request) {
  const response = await api.post("mafia/part", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });

  const normalized = (response.data || []).map((row) => ({
    DNA: row.dna,
    DimensionType: row.dimensionType,
    Unit: row.unit,
    Nominal: row.nominal,
    Min: row.min,
    Max: row.max,
    ToleranceClass: row.toleranceClass,
    InProcess: row.inProcess,
    MethodOfRecording: row.methodOfRecording,
    SpecialInstructions: row.specialInstructions,
    WIATPNumber: row.wiatpNumber,
    Gages: row.gages,
    AdditionalInfo: row.additionalInfo,
    MOMOpNumber: row.momOpNumber,
  }));

  console.log(normalized, "getPart normalized");
  return normalized;
}
async function confirmPart(request) {
  const response = await api.post("mafia/confirm-part", {
    partNum: request.partNum,
    rev: request.revMajor,
    revMinor: request.revMinor,
    newHistory: request.history,
    oldHistory: request.oldHistory,
    revisedBy: request.confirmedBy,
  });

  return response.data;
}

async function sendEmailPartConfirmed(request) {
  const response = await api.post("mafia/email-part-confirmed", {
    fromName: request.fromName,
    partNum: request.partNum,
    revMajor: request.revMajor,
    revMinor: request.revMinor,
  });

  return response.data;
}

async function sendEmailNeedsRelease(request) {
  const response = await api.post("mafia/email-needs-release", {
    fromName: request.fromName,
    partNum: request.partNum,
    revMajor: request.revMajor,
    revMinor: request.revMinor,
  });

  return response.data;
}